import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import LogicGroupRepository from '@/repositories/LogicGroupRepository';
import { LogicGroupPayload } from '@/types/logic';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

import useAPI from './useAPI';

/**
 * The full criteria tree (root LogicGroups, with their nested child groups and
 * elements already recursively serialized) for one subject within one
 * application.
 */
export const useLogicGroups = (applicationId?: string, subjectId?: string) => {
  const logicGroupRepository = LogicGroupRepository();

  return useAPI({
    queryKey: ['getLogicGroups', applicationId, subjectId],
    queryFn: () => logicGroupRepository.list(applicationId as string, subjectId as string),
    enabled: !!applicationId && !!subjectId
  });
};

const useInvalidateLogicGroups = (applicationId?: string, subjectId?: string) => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: ['getLogicGroups', applicationId, subjectId] });
};

export const useCreateLogicGroup = (applicationId?: string, subjectId?: string) => {
  const logicGroupRepository = LogicGroupRepository();
  const invalidateLogicGroups = useInvalidateLogicGroups(applicationId, subjectId);
  const { addMessage } = useContext(AppContext);

  return useMutation({
    mutationFn: (data: LogicGroupPayload) => logicGroupRepository.create(data),
    onSuccess: invalidateLogicGroups,
    onError: (error: AxiosError) => addMessage(mapAxiosErrorMessages(error))
  });
};

export const useUpdateLogicGroup = (applicationId?: string, subjectId?: string) => {
  const logicGroupRepository = LogicGroupRepository();
  const invalidateLogicGroups = useInvalidateLogicGroups(applicationId, subjectId);
  const { addMessage } = useContext(AppContext);

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<LogicGroupPayload> }) =>
      logicGroupRepository.update(id, data),
    onSuccess: invalidateLogicGroups,
    onError: (error: AxiosError) => addMessage(mapAxiosErrorMessages(error))
  });
};

export const useDeleteLogicGroup = (applicationId?: string, subjectId?: string) => {
  const logicGroupRepository = LogicGroupRepository();
  const invalidateLogicGroups = useInvalidateLogicGroups(applicationId, subjectId);
  const { addMessage } = useContext(AppContext);

  return useMutation({
    mutationFn: (id: string) => logicGroupRepository.delete(id),
    onSuccess: invalidateLogicGroups,
    onError: (error: AxiosError) => addMessage(mapAxiosErrorMessages(error))
  });
};

export default useLogicGroups;
